/**
 * composables/useComposerDropTarget.ts —— 全视口拖放目标（U-1d；102 `useComposerDropTarget.js` 逐条译 React+TS）。
 * 依据：升级方案 §2 批 U-1d 行「文件拖到窗口任意位置出现提示层」。
 * 🔴 宿主配合：本件自持 **window 级**监听（不要求宿主挂 dragenter 到主区——旧面挂会话主区的做法不迁移）。
 *   监听装配点＝Composer 组件挂载期（`attach()/detach()` 显式化，单测可只测 handle 回调不打真事件）。
 * 计数语义迁移：旧面 dragCounter（enter/leave 成对计数，防子元素间穿梭把遮罩闪断）——leave 归零才隐藏。
 * 文件类型判定：`dataTransfer.types` 含 'Files' 才算文件拖放（拖选文字不触发提示层）。
 *
 * 🔴 React 附着性（F2 批修根因）：102 Vue `setup` 只跑一次，监听装上就不动；React 每渲染重建闭包 ⇒
 *   若 `attach/detach` 身份随渲染漂移，消费方 `useEffect([dropTarget])` 会**每渲染 detach+attach**，
 *   而 `detach()` 末尾的 `counter=0; dragging=false` 把**进行中的拖放态清掉**（遮罩永远闪不出来）。
 *   落法：`attach/detach/handlers` 一律 `useCallback([])`（身份恒定，装/拆配对成真）；
 *   `onFiles/notice` 走 ref 取**当次渲染**值（不冻结首渲染闭包）。行为语义与 102 逐条一致。
 */
import { useCallback, useMemo, useRef, useState } from "react";

export type NoticeKind = "info" | "warning" | "error";

/** 拖放事件最小面（真浏览器 DragEvent 结构性满足；单测可喂形似对象） */
export interface DragLike {
  dataTransfer?: {
    types?: ArrayLike<string> & { includes?: (v: string) => boolean };
    files?: ArrayLike<File>;
    dropEffect?: string;
  } | null;
  preventDefault?: () => void;
}

export interface UseComposerDropTargetOptions {
  onFiles?: (files: File[]) => void;
  /** 注入 window 形对象（单测替身；缺省＝真 window） */
  target?: { window?: Window | null } | null;
  notice?: (text: string, kind?: NoticeKind) => void;
}

export interface UseComposerDropTarget {
  dragging: boolean;
  attach: () => void;
  detach: () => void;
  handlers: {
    onDragEnter: (event: DragLike) => void;
    onDragOver: (event: DragLike) => void;
    onDragLeave: (event: DragLike) => void;
    onDrop: (event: DragLike) => void;
  };
}

function hasFiles(event: DragLike | undefined | null): boolean {
  const types = event && event.dataTransfer && event.dataTransfer.types;
  if (!types) return false;
  return (
    Array.prototype.indexOf.call(types, "Files") >= 0 ||
    (typeof types.includes === "function" && types.includes("Files"))
  );
}

export function useComposerDropTarget({
  onFiles,
  target = null,
  notice = () => {},
}: UseComposerDropTargetOptions = {}): UseComposerDropTarget {
  const [dragging, setDragging] = useState(false);
  const counterRef = useRef(0);
  // 回调走 ref：handler 身份恒定（可装可拆），但每次都取**当次渲染**的 onFiles/notice
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;
  const noticeRef = useRef(notice);
  noticeRef.current = notice;
  const targetRef = useRef(target);
  targetRef.current = target;
  const el = useCallback(
    () => (targetRef.current && targetRef.current.window) || (typeof window !== "undefined" ? window : null),
    [],
  );

  const onDragEnter = useCallback((event: DragLike): void => {
    if (!hasFiles(event)) return;
    counterRef.current += 1;
    if (counterRef.current === 1) setDragging(true);
  }, []);
  const onDragOver = useCallback((event: DragLike): void => {
    if (!hasFiles(event)) return;
    // dropEffect 赋值仅真浏览器有语义；happy-dom/jsdom 的事件对象没有该属性链，判空跳过（不影响 preventDefault 的落点许可）
    const dt = event.dataTransfer;
    if (dt && "dropEffect" in dt) dt.dropEffect = "copy";
    if (typeof event.preventDefault === "function") event.preventDefault(); // 允许落点（否则浏览器拒绝 drop）
  }, []);
  const onDragLeave = useCallback((event: DragLike): void => {
    if (!hasFiles(event)) return;
    counterRef.current -= 1;
    if (counterRef.current <= 0) {
      counterRef.current = 0;
      setDragging(false);
    }
  }, []);
  const onDrop = useCallback((event: DragLike): void => {
    if (typeof event.preventDefault === "function") event.preventDefault();
    counterRef.current = 0;
    setDragging(false);
    const files = event && event.dataTransfer ? Array.prototype.slice.call(event.dataTransfer.files || []) : [];
    if (files.length && typeof onFilesRef.current === "function") onFilesRef.current(files as File[]);
    else if (!files.length) noticeRef.current("拖放内容中没有文件", "info");
  }, []);

  const attach = useCallback((): void => {
    const w = el() as (Window & typeof globalThis) | null;
    if (!w || typeof w.addEventListener !== "function") return;
    w.addEventListener("dragenter", onDragEnter as unknown as EventListener);
    w.addEventListener("dragover", onDragOver as unknown as EventListener);
    w.addEventListener("dragleave", onDragLeave as unknown as EventListener);
    w.addEventListener("drop", onDrop as unknown as EventListener);
  }, [el, onDragEnter, onDragOver, onDragLeave, onDrop]);
  const detach = useCallback((): void => {
    const w = el() as (Window & typeof globalThis) | null;
    if (!w || typeof w.removeEventListener !== "function") return;
    w.removeEventListener("dragenter", onDragEnter as unknown as EventListener);
    w.removeEventListener("dragover", onDragOver as unknown as EventListener);
    w.removeEventListener("dragleave", onDragLeave as unknown as EventListener);
    w.removeEventListener("drop", onDrop as unknown as EventListener);
    counterRef.current = 0;
    setDragging(false);
  }, [el, onDragEnter, onDragOver, onDragLeave, onDrop]);

  return useMemo(
    () => ({ dragging, attach, detach, handlers: { onDragEnter, onDragOver, onDragLeave, onDrop } }),
    [dragging, attach, detach, onDragEnter, onDragOver, onDragLeave, onDrop],
  );
}

export default useComposerDropTarget;
